import React, { useState, useEffect } from 'react';
import { ArrowLeft, Image as ImageIcon, TrendingUp, Users, Sparkles, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import PostComposer from '../../components/consultant/PostComposer';
import CommunityPost from '../../components/consultant/CommunityPost';
import ConnectionsChat from '../../components/consultant/ConnectionsChat';
import ProfileCard from '../../components/consultant/ProfileCard';

export default function ConsultantCommunity() {
  const { theme, setTheme, setThemeTransient } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'trending' | 'following'>('all');
  const [followerProfile, setFollowerProfile] = useState<any>(null);
  const [followerPhotoUrl, setFollowerPhotoUrl] = useState<string | null>(null);
  const [followerConnCount, setFollowerConnCount] = useState(0);
  const [followerFollowerCount, setFollowerFollowerCount] = useState(0);
  const [showFollowerCard, setShowFollowerCard] = useState(false);

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const followerId = params.get('follower');
    if (followerId) {
      fetchFollowerProfile(followerId);
    }
  }, [location.search]);

  const fetchFollowerProfile = async (followerId: string) => {
    try {
      const { data: profile } = await supabase
        .from('consultant_profiles')
        .select('*')
        .eq('id', followerId)
        .maybeSingle();

      if (profile) {
        setFollowerProfile(profile);

        if (profile.photo_url) {
          if (profile.photo_url.startsWith('http')) {
            setFollowerPhotoUrl(profile.photo_url);
          } else {
            const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(profile.photo_url);
            setFollowerPhotoUrl(urlData.publicUrl);
          }
        } else {
          setFollowerPhotoUrl(null);
        }

        const [connResult, followResult] = await Promise.all([
          supabase
            .from('community_connections')
            .select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${followerId},addressee_id.eq.${followerId}`)
            .eq('status', 'accepted'),
          supabase
            .from('community_followers')
            .select('id', { count: 'exact', head: true })
            .eq('following_id', followerId),
        ]);

        setFollowerConnCount(connResult.count || 0);
        setFollowerFollowerCount(followResult.count || 0);
        setShowFollowerCard(true);
      }
    } catch (err) {
      console.error('Error fetching follower profile:', err);
    }
  };

  useEffect(() => {
    fetchLogo();
    fetchPosts();

    const channel = supabase
      .channel('consultant_community_posts_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'consultant_community_posts' },
        () => {
          fetchPosts();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);

      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setLogoLoading(false);
          return;
        }

        if (data?.value) {
          const img = new Image();
          img.onload = () => {
            setLogoUrl(data.value);
            setLogoLoading(false);
          };
          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setLogoLoading(false);
          };
          img.src = data.value;
        } else {
          setLogoLoading(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setLogoLoading(false);
      }
    } catch (err) {
      setLogoLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('consultant_community_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter === 'trending') {
        query = query.order('likes_count', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = () => {
    fetchPosts();
  };

  const handlePostDeleted = () => {
    fetchPosts();
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark'
        ? 'bg-gray-900 text-white'
        : 'bg-gray-50 text-gray-900'
    }`}>
      <ConnectionsChat theme={theme} />

      {showFollowerCard && followerProfile && (
        <ProfileCard
          profile={followerProfile}
          photoUrl={followerPhotoUrl}
          connectionsCount={followerConnCount}
          followersCount={followerFollowerCount}
          onClose={() => {
            setShowFollowerCard(false);
            navigate('/consultant/community', { replace: true });
          }}
          theme={theme}
        />
      )}
      {/* Header */}
      <header className="fixed w-full z-50 bg-black backdrop-blur-md shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex-1 flex items-center justify-between">
              <div className="text-white -mt-4 -mb-4 cursor-default select-none">
                {logoLoading ? (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : logoUrl ? (
                  <>
                    <img
                      src={logoUrl}
                      alt="Perplexity"
                      className="h-32 md:h-28 w-auto object-contain max-w-[500px] md:max-w-[500px]"
                      onError={(e) => {
                        console.warn('Error loading logo image, falling back to text');
                        e.currentTarget.style.display = 'none';
                        const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (textFallback) {
                          textFallback.style.display = 'block';
                        }
                      }}
                    />
                    <div
                      className="h-32 md:h-28 flex items-center justify-center text-primary text-2xl font-bold"
                      style={{ display: 'none' }}
                    >
                      PERPLEXITY
                    </div>
                  </>
                ) : (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center text-primary">
                    <ImageIcon size={32} />
                  </div>
                )}
              </div>

              <button
                onClick={() => navigate('/consultant/dashboard')}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#40E0D0] hover:bg-[#35c5b5] text-white font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-32 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Workday Community Connect
            </h1>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Connect, share, and learn with fellow Workday consultants
            </p>
          </div>

          {/* Filter Tabs */}
          <div className={`flex items-center space-x-2 mb-6 p-1 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <button
              onClick={() => setFilter('all')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                filter === 'all'
                  ? 'bg-[#40E0D0] text-white'
                  : theme === 'dark'
                  ? 'text-gray-400 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span className="font-medium">All Posts</span>
            </button>
            <button
              onClick={() => {
                setFilter('trending');
                fetchPosts();
              }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                filter === 'trending'
                  ? 'bg-[#40E0D0] text-white'
                  : theme === 'dark'
                  ? 'text-gray-400 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              <span className="font-medium">Trending</span>
            </button>
          </div>

          {/* Post Composer */}
          <PostComposer onPostCreated={handlePostCreated} theme={theme} />

          {/* Posts Feed */}
          <div className="mt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className={`text-center py-12 rounded-xl ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              }`}>
                <Users className={`w-16 h-16 mx-auto mb-4 ${
                  theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                }`} />
                <h3 className={`text-xl font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  No posts yet
                </h3>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Be the first to share something with the community!
                </p>
              </div>
            ) : (
              posts.map((post) => (
                <CommunityPost
                  key={post.id}
                  post={post}
                  onDelete={handlePostDeleted}
                  onUpdate={fetchPosts}
                  theme={theme}
                />
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
